/* ==========================================================================
   Zooted Prestige — product-finder.js
   "Configure Your Experience" guided recommendation tool.
   All content lives in product-finder-data.js (window.ZOOTED_FINDER).
   No medical claims — recommendations are lifestyle / flavor matches only.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-finder]');
  var data = window.ZOOTED_FINDER;
  if (!root || !data || !data.questions || !data.questions.length) return;

  var placeholderMap = JSON.parse(root.getAttribute('data-finder-placeholders') || '{}');
  var questions = data.questions;
  var state = { step: 0, answers: {} };
  questions.forEach(function (q) { state.answers[q.id] = []; });

  var stepsWrap = root.querySelector('[data-finder-steps]');
  var resultsWrap = root.querySelector('[data-finder-results]');
  var progressWrap = root.querySelector('[data-finder-progress]');
  var live = root.querySelector('[data-finder-live]');

  /* ------------------------------------------------------------------
     Render
     ------------------------------------------------------------------ */
  function renderProgress() {
    progressWrap.innerHTML = questions.map(function (_, i) {
      var cls = i < state.step ? 'is-done' : (i === state.step ? 'is-current' : '');
      return '<span class="' + cls + '"></span>';
    }).join('');
  }

  function renderSteps() {
    stepsWrap.innerHTML = questions.map(function (q, qi) {
      var options = q.options.map(function (opt, oi) {
        var pressed = state.answers[q.id].indexOf(oi) !== -1;
        return (
          '<button type="button" class="finder-option" data-q="' + qi + '" data-o="' + oi + '" ' +
          'aria-pressed="' + pressed + '">' + opt.label + '</button>'
        );
      }).join('');
      return (
        '<div class="finder__step' + (qi === state.step ? ' is-active' : '') + '" data-step="' + qi + '"' +
        (qi === state.step ? '' : ' hidden') + ' role="group" aria-labelledby="FinderQ' + qi + '">' +
          '<h3 class="finder__question h-display h-md" id="FinderQ' + qi + '">' + q.title + '</h3>' +
          (q.hint ? '<p class="finder__hint">' + q.hint + (q.multiple ? '' : '') + '</p>' : '') +
          '<div class="finder__options">' + options + '</div>' +
        '</div>'
      );
    }).join('');
    renderNav();
    renderProgress();
  }

  function renderNav() {
    var nav = root.querySelector('[data-finder-nav]');
    var atStart = state.step === 0;
    var q = questions[state.step];
    var hasAnswer = q && state.answers[q.id].length > 0;
    nav.innerHTML =
      '<button type="button" class="btn btn--outline btn--sm" data-finder-back' + (atStart ? ' disabled' : '') + '>' +
        'Back</button>' +
      '<div style="display:flex;gap:0.8rem;flex-wrap:wrap;">' +
        '<button type="button" class="btn btn--sm btn--outline" data-finder-restart>Restart</button>' +
        (q && q.multiple
          ? '<button type="button" class="btn btn--sm" data-finder-next' + (hasAnswer ? '' : ' disabled') + '>Continue</button>'
          : '') +
      '</div>';
  }

  function announce(msg) { if (live) { live.textContent = ''; setTimeout(function () { live.textContent = msg; }, 50); } }

  function goTo(step) {
    state.step = Math.max(0, Math.min(questions.length, step));
    if (state.step >= questions.length) { showResults(); return; }
    resultsWrap.classList.remove('is-active');
    resultsWrap.hidden = true;
    stepsWrap.hidden = false;
    renderSteps();
    announce('Question ' + (state.step + 1) + ' of ' + questions.length + ': ' + questions[state.step].title);
    var heading = stepsWrap.querySelector('.finder__step.is-active .finder__question');
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: false }); }
  }

  /* ------------------------------------------------------------------
     Scoring
     ------------------------------------------------------------------ */
  function collectTags() {
    var picked = {}; // tag -> weight
    var readable = [];
    questions.forEach(function (q) {
      var weight = (data.weights && data.weights[q.id]) || 1;
      state.answers[q.id].forEach(function (oi) {
        var opt = q.options[oi];
        readable.push(opt.label.toLowerCase());
        (opt.tags || []).forEach(function (t) {
          picked[t] = Math.max(picked[t] || 0, weight);
        });
      });
    });
    return { tags: picked, readable: readable };
  }

  function scoreProducts() {
    var picked = collectTags();
    var scored = (data.products || []).map(function (p) {
      var score = 0;
      (p.tags || []).forEach(function (t) { if (picked.tags[t]) score += picked.tags[t]; });
      return { product: p, score: score };
    }).sort(function (a, b) { return b.score - a.score; });
    var top = scored.filter(function (s) { return s.score > 0; }).slice(0, data.maxResults || 3);
    if (!top.length) top = scored.slice(0, data.maxResults || 3); // "just browsing" fallback
    return { top: top, readable: picked.readable };
  }

  function resolveImage(ref) {
    if (!ref) return '';
    if (ref.indexOf('placeholder:') === 0) return placeholderMap[ref.replace('placeholder:', '')] || '';
    return ref;
  }

  function showResults() {
    var result = scoreProducts();
    var summary = result.readable.slice(0, 6).join(', ') || 'your selections';
    var findUrl = root.getAttribute('data-find-url') || '/pages/find-zooted';

    resultsWrap.innerHTML =
      '<p class="eyebrow eyebrow--magenta">Your matches</p>' +
      '<h3 class="h-display h-lg">RECOMMENDED FOR YOU</h3>' +
      '<p class="finder__hint">Based on: ' + summary + '. Recommendations describe flavor and lifestyle fit only.</p>' +
      result.top.map(function (s) {
        var p = s.product;
        var img = resolveImage(p.image);
        return (
          '<article class="finder-result">' +
            '<div class="finder-result__media">' + (img ? '<img src="' + img + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '') + '</div>' +
            '<div>' +
              '<p class="eyebrow">' + p.category + '</p>' +
              '<h4 class="h-md" style="margin-bottom:0.4rem;">' + p.title + '</h4>' +
              '<div class="finder-result__tags">' +
                '<span class="pill-strain pill-strain--' + (String(p.strain).toLowerCase().indexOf('sativa') === 0 ? 'sativa' : String(p.strain).toLowerCase().indexOf('indica') === 0 ? 'indica' : 'hybrid') + '">' + p.strain + '</span>' +
                '<span class="badge badge--outline">' + p.flavors + '</span>' +
              '</div>' +
              '<p class="text-mid">' + p.description + '</p>' +
              '<p class="finder-result__why"><strong>Why it matched:</strong> you selected ' + summary + '.</p>' +
              '<div class="finder-result__actions">' +
                '<a class="btn btn--sm" href="' + p.url + '">View Product</a>' +
                '<a class="btn btn--sm btn--outline" href="' + findUrl + '">Find Near You</a>' +
              '</div>' +
            '</div>' +
          '</article>'
        );
      }).join('') +
      '<div class="finder__nav">' +
        '<button type="button" class="btn btn--outline btn--sm" data-finder-back>Back</button>' +
        '<div style="display:flex;gap:0.8rem;flex-wrap:wrap;">' +
          '<button type="button" class="btn btn--outline btn--sm" data-finder-share>Copy results link</button>' +
          '<button type="button" class="btn btn--sm" data-finder-restart>Start over</button>' +
        '</div>' +
      '</div>';

    stepsWrap.hidden = true;
    resultsWrap.hidden = false;
    resultsWrap.classList.add('is-active');
    progressWrap.querySelectorAll('span').forEach(function (s) { s.classList.add('is-done'); s.classList.remove('is-current'); });
    announce('Recommendations ready.');
    var heading = resultsWrap.querySelector('h3');
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus(); }

    // shareable link
    var params = new URLSearchParams();
    questions.forEach(function (q) {
      if (state.answers[q.id].length) params.set('f_' + q.id, state.answers[q.id].join('.'));
    });
    history.replaceState(null, '', window.location.pathname + '?' + params.toString() + '#finder');
  }

  /* ------------------------------------------------------------------
     Events
     ------------------------------------------------------------------ */
  root.addEventListener('click', function (e) {
    var opt = e.target.closest('.finder-option');
    if (opt) {
      var qi = Number(opt.getAttribute('data-q'));
      var oi = Number(opt.getAttribute('data-o'));
      var q = questions[qi];
      var current = state.answers[q.id];
      if (q.multiple) {
        var idx = current.indexOf(oi);
        idx === -1 ? current.push(oi) : current.splice(idx, 1);
        opt.setAttribute('aria-pressed', idx === -1 ? 'true' : 'false');
        renderNav();
      } else {
        state.answers[q.id] = [oi];
        setTimeout(function () { goTo(state.step + 1); }, 220);
      }
      return;
    }
    if (e.target.closest('[data-finder-next]')) goTo(state.step + 1);
    if (e.target.closest('[data-finder-back]')) goTo(Math.min(state.step, questions.length) - 1);
    if (e.target.closest('[data-finder-restart]')) {
      questions.forEach(function (q) { state.answers[q.id] = []; });
      history.replaceState(null, '', window.location.pathname + '#finder');
      goTo(0);
    }
    var share = e.target.closest('[data-finder-share]');
    if (share && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(function () {
        share.textContent = 'Link copied';
        setTimeout(function () { share.textContent = 'Copy results link'; }, 2000);
      });
    }
  });

  /* Swipe between steps on touch devices */
  var touchX = null;
  root.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
  root.addEventListener('touchend', function (e) {
    if (touchX === null || state.step >= questions.length) return;
    var dx = e.changedTouches[0].clientX - touchX;
    var q = questions[state.step];
    if (dx < -70 && state.answers[q.id].length) goTo(state.step + 1);
    if (dx > 70 && state.step > 0) goTo(state.step - 1);
    touchX = null;
  }, { passive: true });

  /* Restore shared answers from URL */
  (function restore() {
    var params = new URLSearchParams(window.location.search);
    var restored = false;
    questions.forEach(function (q) {
      var raw = params.get('f_' + q.id);
      if (raw !== null) {
        state.answers[q.id] = raw.split('.').map(Number).filter(function (n) {
          return !isNaN(n) && n >= 0 && n < q.options.length;
        });
        restored = true;
      }
    });
    if (restored) { state.step = questions.length; showResults(); }
    else renderSteps();
  })();
})();
